import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useNavigate } from './useNavigate';

type RouteType = Parameters<ReturnType<typeof useRouter>['push']>[0];

type UseRedirectWhenOptions = {
  condition?: boolean;
  replace?: boolean;
  to?: RouteType;
  externalTo?: string;
};

/**
 * Hook for conditional redirects based on data or state
 * More explicit and testable than scattered useEffect calls
 * Supports external URLs (beginning with http:// or https://)
 */
export function useRedirectWhen({ condition = true, replace = true, to, externalTo }: UseRedirectWhenOptions): void {
  const router = useRouter();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  if (to && externalTo) {
    throw new Error('Cannot use both to and externalTo in useRedirectWhen');
  }

  useEffect(() => {
    if (hasRedirected.current) return;

    if (condition) {
      hasRedirected.current = true;

      if (externalTo) {
        window.location.href = externalTo;
      } else if (to) {
        const internalRoute = to;
        if (replace) {
          navigate(internalRoute, { replace: true });
        } else {
          navigate(internalRoute);
        }
      } else {
        throw new Error('Must use either assign "to" or "externalTo" in useRedirectWhen');
      }
    }
  }, [condition, to, replace, router, navigate, externalTo]);
}
